/**
 * ErrorPanel.tsx
 *
 * Persistent on-screen error notification panel.
 *
 * Errors appear as cards in the bottom-right corner of the screen.
 * They stay visible until the user dismisses them — unlike toasts
 * that auto-disappear — so users can take a screenshot and share
 * the reference ID with the support team.
 *
 * Two card styles:
 *  - Validation errors (isValidationError: true): amber/warning — user-fixable,
 *    shows all backend messages as a bullet list.
 *  - System errors: red — unexpected failures, shows friendly description.
 *
 * Each card shows:
 *  - Friendly error title & description / message list
 *  - A short reference ID (e.g. "Ref: 1K9F3A2B") for support tracing
 *  - A "Copy details" button
 *  - A dismiss (×) button
 *
 * Mount once in App.tsx at the root level.
 */
import { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Collapse,
  Badge,
  Button,
  Divider,
} from '@mui/material';
import { AlertTriangle, Info, X, Copy, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { useErrorStore, AppError } from '../../store/errorStore';

// ─── Single error card ────────────────────────────────────────────────────────

function ErrorCard({ error, onDismiss }: { error: AppError; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);

  const isValidation = error.isValidationError === true;
  const accentColor = isValidation ? '#d97706' : '#ef4444';
  const bgColor = isValidation ? '#fffbeb' : '#fef2f2';
  const iconColor = isValidation ? '#d97706' : '#ef4444';

  const copyDetails = () => {
    const messageLines =
      error.messages && error.messages.length > 1
        ? error.messages.map((m, i) => `  ${i + 1}. ${m}`).join('\n')
        : `  ${error.description}`;

    const lines = [
      `Error: ${error.title}`,
      `Messages:\n${messageLines}`,
      `Reference ID: ${error.referenceId}`,
      ...(error.requestId ? [`Request ID: ${error.requestId}`] : []),
      ...(error.backendTraceId ? [`Backend Trace ID: ${error.backendTraceId}`] : []),
      `Time: ${new Date(error.timestamp).toLocaleString()}`,
    ];

    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Determine what to show in the body:
  // - Validation errors with multiple messages → bullet list
  // - Validation errors with one message → description only
  // - System errors → description only
  const hasMultipleMessages = isValidation && error.messages && error.messages.length > 1;

  return (
    <Paper
      elevation={4}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: 380,
        borderLeft: `4px solid ${accentColor}`,
        borderRadius: '8px',
        overflow: 'hidden',
        mb: 1,
        bgcolor: '#fff',
      }}
    >
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', p: '12px 12px 8px', gap: 1 }}>
        {isValidation
          ? <Info size={18} color={iconColor} style={{ flexShrink: 0, marginTop: 2 }} />
          : <AlertTriangle size={18} color={iconColor} style={{ flexShrink: 0, marginTop: 2 }} />
        }

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: '#1f2937', lineHeight: 1.3 }}
          >
            {error.title}
          </Typography>

          {hasMultipleMessages ? (
            // Multiple backend messages: show as a numbered list
            <Box
              component="ol"
              sx={{
                m: 0,
                pl: '18px',
                mt: 0.5,
              }}
            >
              {error.messages!.map((msg, i) => (
                <Typography
                  key={i}
                  component="li"
                  variant="body2"
                  sx={{ color: '#4b5563', lineHeight: 1.5, fontSize: '0.8rem' }}
                >
                  {msg}
                </Typography>
              ))}
            </Box>
          ) : (
            <Typography
              variant="body2"
              sx={{ color: '#4b5563', mt: 0.5, lineHeight: 1.5, fontSize: '0.8rem' }}
            >
              {error.description}
            </Typography>
          )}
        </Box>

        <IconButton
          size="small"
          onClick={onDismiss}
          aria-label="Dismiss error"
          sx={{ flexShrink: 0, color: '#9ca3af', '&:hover': { color: '#374151' } }}
        >
          <X size={16} />
        </IconButton>
      </Box>

      <Divider />

      {/* Footer: reference ID + optional trace IDs + copy */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          px: '12px',
          py: '6px',
          bgcolor: bgColor,
          gap: 0.25,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.15 }}>
            <Typography
              variant="caption"
              sx={{ color: '#6b7280', fontFamily: 'monospace', letterSpacing: '0.04em', fontSize: '0.68rem' }}
            >
              Ref:&nbsp;<strong>{error.referenceId}</strong>
            </Typography>
            {error.requestId && (
              <Typography
                variant="caption"
                sx={{ color: '#9ca3af', fontFamily: 'monospace', letterSpacing: '0.04em', fontSize: '0.68rem' }}
              >
                Req:&nbsp;<span>{error.requestId}</span>
              </Typography>
            )}
            {error.backendTraceId && (
              <Typography
                variant="caption"
                sx={{ color: '#9ca3af', fontFamily: 'monospace', letterSpacing: '0.04em', fontSize: '0.68rem' }}
              >
                Trace:&nbsp;<span>{error.backendTraceId}</span>
              </Typography>
            )}
          </Box>

          <Tooltip title={copied ? 'Copied!' : 'Copy details for support'} placement="top">
            <IconButton
              size="small"
              onClick={copyDetails}
              aria-label="Copy error details"
              sx={{ color: copied ? '#16a34a' : '#6b7280', p: '4px', alignSelf: 'flex-start' }}
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
}

// ─── Panel container ──────────────────────────────────────────────────────────

export default function ErrorPanel() {
  const { errors, removeError, clearAll } = useErrorStore();
  const [collapsed, setCollapsed] = useState(false);

  if (errors.length === 0) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 9999,
        maxWidth: 400,
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
      }}
    >
      {/* Collapse/expand toggle when multiple errors exist */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 0.5,
          px: 1,
        }}
      >
        {errors.length > 1 && (
          <Button
            size="small"
            variant="text"
            onClick={clearAll}
            sx={{
              fontSize: '0.7rem',
              color: '#6b7280',
              textTransform: 'none',
              minWidth: 0,
              p: '2px 6px',
            }}
          >
            Clear all
          </Button>
        )}

        <Badge badgeContent={errors.length} color="error" sx={{ cursor: 'pointer' }}>
          <IconButton
            size="small"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'Expand errors' : 'Collapse errors'}
            sx={{
              bgcolor: '#ef4444',
              color: '#fff',
              width: 28,
              height: 28,
              '&:hover': { bgcolor: '#dc2626' },
            }}
          >
            {collapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </IconButton>
        </Badge>
      </Box>

      {/* Error cards */}
      <Collapse in={!collapsed} timeout={200}>
        <Box
          sx={{
            overflowY: 'auto',
            maxHeight: 'calc(80vh - 48px)',
            pr: 0.5,
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#e5e7eb', borderRadius: 2 },
          }}
        >
          {errors.map((error) => (
            <ErrorCard
              key={error.id}
              error={error}
              onDismiss={() => removeError(error.id)}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}
